import { FormEvent, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { adminApi, type Org } from "../../lib/admin";
import { download } from "../../lib/reports";
import { Drawer, Field, Notice, Pill, Toolbar, fmtDate } from "./ui";

/** Fiji VAT on subscription fees. Confirm the rate with FRCS / the accountant before invoicing. */
export const VAT_RATE = 0.125;
/** V3.9.5: plan prices are VAT-INCLUSIVE. Split a VAT-inclusive amount into excl. VAT + VAT (rounded to cents; VAT = total − excl.). */
export function splitVat(incl: number) {
  const total = Math.round(incl * 100) / 100;
  const excl = Math.round((total / (1 + VAT_RATE)) * 100) / 100;
  return { total, excl, vat: Math.round((total - excl) * 100) / 100 };
}

export type Plan = { id: string; name: string; price_per_device: number; description: string | null; active: boolean };
type Dev = { id: string; name: string; unique_id: string; org_id: string; status: string; created_at: string;
  plan_id: string | null; no_charge: boolean | null; is_simulated: boolean | null };
/** V3.9.4 — what one device costs per month (incl. VAT since V3.9.5): its own plan, else the company plan; demo / no-charge / not active = not billed. */
type DevBill = { d: Dev; billed: boolean; rate: number | null; plan: Plan | null; own: boolean; why: string | null };
function billOf(d: Dev, orgPlan: Plan | null, plans: Plan[]): DevBill {
  const own = d.plan_id ? plans.find((p) => p.id === d.plan_id) ?? null : null;
  if (d.status !== "active") return { d, billed: false, rate: null, plan: own ?? orgPlan, own: !!own, why: "Not active" };
  if (d.is_simulated) return { d, billed: false, rate: null, plan: null, own: false, why: "Demo device" };
  if (d.no_charge) return { d, billed: false, rate: null, plan: null, own: false, why: "No charge" };
  const plan = own ?? orgPlan;
  return plan ? { d, billed: true, rate: plan.price_per_device, plan, own: !!own, why: null } : { d, billed: false, rate: null, plan: null, own: false, why: "No plan" };
}

const fjd = (n: number) => `FJD ${n.toLocaleString("en-FJ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const money = (n: number) => n.toLocaleString("en-FJ", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Admin → Plans: define per-device monthly plans, put client companies on a plan, and see the active
 * (billable) devices each company is paying for. Billable = device status "active" (suspended/retired don't count).
 */
export default function Plans({ orgs, reloadOrgs }: { orgs: Org[]; reloadOrgs: () => Promise<void> }) {
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [devs, setDevs] = useState<Dev[]>([]);
  const [edit, setEdit] = useState<Plan | "new" | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: "ok" | "bad"; text: string } | null>(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    const [{ data: p }, { data: d }] = await Promise.all([
      supabase.from("plans").select("*").order("price_per_device"),
      supabase.rpc("admin_devices"),
    ]);
    setPlans(((p ?? []) as any[]).map((x) => ({ ...x, price_per_device: Number(x.price_per_device) })));
    setDevs(((d ?? []) as any[]).filter((x) => x.status !== "retired") as Dev[]);
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => orgs.filter((o) => o.status !== "closed" && (showAll || o.type === "client")).map((o) => {
    const plan = plans?.find((p) => p.id === o.plan_id) ?? null;
    const mine = devs.filter((d) => d.org_id === o.id);
    const bills = mine.map((d) => billOf(d, plan, plans ?? []));
    const billable = bills.filter((b) => b.billed);
    const money3 = splitVat(billable.reduce((a, b) => a + (b.rate ?? 0), 0));   // rates are VAT-inclusive
    const own = billable.filter((b) => b.own).length;
    const unpriced = bills.filter((b) => b.why === "No plan").length;
    return { o, plan, mine, bills, billable, own, unpriced, sub: money3.excl, vat: money3.vat, total: money3.total };
  }), [orgs, plans, devs, showAll]);
  const sum = rows.reduce((a, r) => ({ devices: a.devices + r.billable.length, sub: a.sub + r.sub, vat: a.vat + r.vat, total: a.total + r.total }),
    { devices: 0, sub: 0, vat: 0, total: 0 });
  sum.sub = Math.round(sum.sub * 100) / 100; sum.vat = Math.round(sum.vat * 100) / 100; sum.total = Math.round(sum.total * 100) / 100;
  const noPlan = rows.filter((r) => r.unpriced > 0 && r.o.type === "client");

  async function setBilling(d: Dev, v: string) {
    setMsg(null);
    const body = v === "company" ? { mode: "company" } : v === "free" ? { mode: "free" } : { mode: "plan", plan_id: v };
    try {
      await adminApi("PATCH", `devices/${d.id}/billing`, body);
      await load();
      setMsg({ kind: "ok", text: `${d.name}: ${v === "company" ? "follows the company plan" : v === "free" ? "no charge" : `own plan ${plans?.find((p) => p.id === v)?.name}`}.` });
    } catch (x: any) { setMsg({ kind: "bad", text: x.message }); }
  }

  async function assign(o: Org, planId: string) {
    setMsg(null);
    try {
      await adminApi("PATCH", `orgs/${o.id}`, { plan_id: planId || null });
      await reloadOrgs();
      setMsg({ kind: "ok", text: planId ? `${o.name} is now on ${plans?.find((p) => p.id === planId)?.name}.` : `${o.name} has no plan.` });
    } catch (x: any) { setMsg({ kind: "bad", text: x.message }); }
  }

  function exportCsv() {
    const month = new Intl.DateTimeFormat("en-CA", { timeZone: "Pacific/Fiji", year: "numeric", month: "2-digit" }).format(new Date());
    const lines = [["Company", "Plan", "Rate per device incl. VAT (FJD)", "Active devices", "Subtotal excl. VAT (FJD)", `VAT ${VAT_RATE * 100}% (FJD)`, "Total incl. VAT (FJD)", "Devices"]];
    for (const r of rows) lines.push([r.o.name, r.plan?.name ?? "", r.plan ? money(r.plan.price_per_device) : "", String(r.billable.length),
      money(r.sub), money(r.vat), money(r.total),
      r.billable.map((b) => `${b.d.name} (${b.d.unique_id})${b.own ? ` [${b.plan!.name} ${money(b.rate!)}]` : ""}`).join("; ")]);
    const esc = (v: string) => (/[",\n;]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
    download(`TrackingFiji_Billing_${month}.csv`, "﻿" + lines.map((l) => l.map(esc).join(",")).join("\r\n"));
  }

  return (
    <section>
      <Toolbar>
        <h1>Plans &amp; billing</h1>
        <label className="acheck"><input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} /> Include Skyward (internal)</label>
        <button className="btn-ghost" onClick={exportCsv} disabled={!plans}>Export CSV</button>
        <button className="btn-primary" onClick={() => setEdit("new")}>+ Add plan</button>
      </Toolbar>
      {msg && <Notice kind={msg.kind} onClose={() => setMsg(null)}>{msg.text}</Notice>}

      <div className="plan-cards">
        {plans?.map((p) => {
          const n = orgs.filter((o) => o.plan_id === p.id).length;
          const nd = devs.filter((d) => d.plan_id === p.id).length;
          return (
            <button key={p.id} className={`plan-card ${p.active ? "" : "off"}`} onClick={() => setEdit(p)}>
              <div className="plan-top"><b>{p.name}</b>{!p.active && <Pill value="suspended" label="Inactive" />}</div>
              <div className="plan-price mono">{fjd(p.price_per_device)}<span> / device / month</span></div>
              <div className="sub">incl. VAT ({fjd(splitVat(p.price_per_device).excl)} excl.) · {n} compan{n === 1 ? "y" : "ies"}{nd ? ` · ${nd} device${nd === 1 ? "" : "s"} on their own` : ""}</div>
              {p.description && <div className="plan-desc">{p.description}</div>}
            </button>
          );
        })}
        {plans && !plans.length && <div className="rep-empty">No plans yet — add your first plan (for example “Standard”, FJD 35 per device per month).</div>}
      </div>

      <div className="rep-tiles bill-tiles">
        <div><b className="mono">{sum.devices}</b><span>Billable devices</span></div>
        <div><b className="mono">{fjd(sum.sub)}</b><span>Monthly subtotal (excl. VAT)</span></div>
        <div><b className="mono">{fjd(sum.vat)}</b><span>VAT {VAT_RATE * 100}%</span></div>
        <div><b className="mono">{fjd(sum.total)}</b><span>Monthly total (incl. VAT)</span></div>
      </div>
      {noPlan.length > 0 && <Notice kind="warn">{noPlan.length} client compan{noPlan.length === 1 ? "y has" : "ies have"} active devices without a plan (not billed): {noPlan.map((r) => r.o.name).join(", ")}.</Notice>}

      <div className="atable-wrap">
        <table className="atable bill">
          <thead><tr><th>Company</th><th>Plan</th><th className="num">Rate</th><th className="num">Active devices</th>
            <th className="num">Subtotal</th><th className="num">VAT</th><th className="num">Total incl. VAT</th><th /></tr></thead>
          <tbody>
            {rows.map((r) => (
              <Fragment key={r.o.id}>
                <tr className={open === r.o.id ? "sel" : ""}>
                  <td><b>{r.o.name}</b>{r.o.status === "suspended" && <span className="sub"> (suspended)</span>}<div className="sub">{r.o.type === "internal" ? "Skyward" : "Client"}{r.o.billing_start ? ` · billing from ${fmtDate(r.o.billing_start)}` : ""}</div></td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <select className="plan-select" value={r.o.plan_id ?? ""} onChange={(e) => assign(r.o, e.target.value)}>
                      <option value="">— No plan —</option>
                      {plans?.filter((p) => p.active || p.id === r.o.plan_id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </td>
                  <td className="num mono">{r.plan ? money(r.plan.price_per_device) : "—"}{r.own > 0 && <div className="sub">{r.own} on own plan</div>}</td>
                  <td className="num mono">{r.billable.length}{r.mine.length > r.billable.length && <span className="sub"> +{r.mine.length - r.billable.length} not billed</span>}</td>
                  <td className="num mono">{r.billable.length ? money(r.sub) : "—"}</td>
                  <td className="num mono">{r.billable.length ? money(r.vat) : "—"}</td>
                  <td className="num mono"><b>{r.billable.length ? money(r.total) : "—"}</b></td>
                  <td className="num"><button className="btn-ghost small" onClick={() => setOpen(open === r.o.id ? null : r.o.id)}>{open === r.o.id ? "Hide" : "Devices"}</button></td>
                </tr>
                {open === r.o.id && (
                  <tr className="bill-devs"><td colSpan={8}>
                    {!r.mine.length ? <span className="sub">No devices registered for this company.</span> : (
                      <table className="mini">
                        <thead><tr><th>Device</th><th>ID</th><th>Status</th><th>Registered</th><th>Billing</th><th className="num">Monthly (incl. VAT)</th></tr></thead>
                        <tbody>
                          {r.bills.map((b) => { const d = b.d; return (
                            <tr key={d.id} className={b.billed ? "" : "dim-row"}>
                              <td>{d.name}</td><td className="mono">{d.unique_id}</td><td><Pill value={d.status} /></td><td>{fmtDate(d.created_at)}</td>
                              <td>{d.is_simulated ? <span className="sub">Demo — never billed</span> : (
                                <select className="plan-select dev-plan" aria-label={`Billing for ${d.name}`}
                                  value={d.no_charge ? "free" : d.plan_id ?? "company"} onChange={(e) => setBilling(d, e.target.value)}>
                                  <option value="company">Company plan{r.plan ? ` (${r.plan.name})` : " (none)"}</option>
                                  {plans?.filter((p) => p.active || p.id === d.plan_id).map((p) => <option key={p.id} value={p.id}>{p.name} · {money(p.price_per_device)}</option>)}
                                  <option value="free">No charge</option>
                                </select>)}</td>
                              <td className="num mono">{b.billed ? money(b.rate!) : <span className="sub">{b.why}</span>}</td>
                            </tr>); })}
                        </tbody>
                      </table>
                    )}
                  </td></tr>
                )}
              </Fragment>
            ))}
            {!rows.length && <tr className="empty"><td colSpan={8}>No companies.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="fine" style={{ textAlign: "left" }}>
        Billable = devices with status “Active” at their own plan's rate, or the company plan's. Suspended, retired, demo and “No charge” devices are not charged. Plan prices include VAT {VAT_RATE * 100}%; the subtotal and VAT are worked out from the VAT-inclusive total. Amounts in FJD — confirm the VAT treatment with your accountant/FRCS before invoicing.
      </p>
      {edit && <PlanForm plan={edit === "new" ? null : edit} onClose={() => setEdit(null)}
        onSaved={async (t) => { setEdit(null); setMsg({ kind: "ok", text: t }); await load(); await reloadOrgs(); }} />}
    </section>
  );
}

function PlanForm({ plan, onClose, onSaved }: { plan: Plan | null; onClose: () => void; onSaved: (msg: string) => void }) {
  const [f, setF] = useState({ name: plan?.name ?? "", price: plan ? String(plan.price_per_device) : "", description: plan?.description ?? "", active: plan?.active ?? true });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const price = Number(f.price);
  async function save(e: FormEvent) {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      const body = { name: f.name, price_per_device: price, description: f.description, active: f.active };
      if (plan) await adminApi("PATCH", `plans/${plan.id}`, body); else await adminApi("POST", "plans", body);
      onSaved(plan ? `Saved plan ${f.name}.` : `Added plan ${f.name}.`);
    } catch (x: any) { setErr(x.message); } finally { setBusy(false); }
  }
  async function del() {
    if (!plan || !confirm(`Delete plan ${plan.name}?`)) return;
    try { await adminApi("DELETE", `plans/${plan.id}`); onSaved(`Deleted plan ${plan.name}.`); } catch (x: any) { setErr(x.message); }
  }
  return (
    <Drawer title={plan ? plan.name : "Add plan"} onClose={onClose}
      footer={<>
        {plan && <button className="btn-ghost danger" onClick={del} style={{ marginRight: "auto" }}>Delete</button>}
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" form="plan-form" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
      </>}>
      <form id="plan-form" className="aform" onSubmit={save}>
        <Field label="Plan name *" wide><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required maxLength={60} placeholder="e.g. Standard" /></Field>
        <Field label={`Price per active device per month (FJD, incl. VAT ${VAT_RATE * 100}%) *`} wide
          hint={Number.isFinite(price) && price > 0 ? `= ${fjd(splitVat(price).excl)} excl. VAT + ${fjd(splitVat(price).vat)} VAT` : "The price the customer pays, VAT included."}>
          <input value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} required inputMode="decimal" placeholder="35.00" className="mono" />
        </Field>
        <Field label="Description" wide hint="What's included, e.g. live tracking, history 90 days, reports, alerts.">
          <textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} maxLength={300} />
        </Field>
        <label className="acheck wide"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Available for new companies</label>
        {err && <div className="error wide">{err}</div>}
      </form>
    </Drawer>
  );
}
